import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { applyBranding, fetchBranding, type Branding, type FetchLike } from "./branding";
import { isE2eMockMode } from "./e2eFixture";

export interface BrandingState {
  /** `loading` until the first `/v1/branding` answer (or failure). */
  status: "loading" | "ready";
  /** Null: SemantiCAD defaults (apex host, unknown host, or fetch failed). */
  branding: Branding | null;
  /** True once the engine confirmed this host is an org workspace. */
  isOrgHost: boolean;
  /** Re-read branding past the HTTP cache (after an owner saves it). */
  refresh: () => Promise<void>;
}

const noop = async () => {};

/** Outside the provider (unit tests, isolated renders): plain SemantiCAD. */
export const BrandingContext = createContext<BrandingState>({
  status: "ready",
  branding: null,
  isOrgHost: false,
  refresh: noop,
});

export function BrandingProvider({
  children,
  fetchImpl,
}: {
  children: ReactNode;
  fetchImpl?: FetchLike;
}) {
  const [status, setStatus] = useState<BrandingState["status"]>("loading");
  const [branding, setBranding] = useState<Branding | null>(null);

  const load = useCallback(
    async (fresh: boolean) => {
      // ?e2e=1 is offline by contract: no engine reads, plain SemantiCAD.
      const next = isE2eMockMode() ? null : await fetchBranding({ fetchImpl, fresh });
      setBranding(next);
      setStatus("ready");
      applyBranding(next);
    },
    [fetchImpl],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  const value = useMemo<BrandingState>(
    () => ({
      status,
      branding,
      isOrgHost: branding !== null,
      refresh: () => load(true),
    }),
    [status, branding, load],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingState {
  return useContext(BrandingContext);
}
